
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import confetti from 'canvas-confetti';

const QuizResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams();

  // Destructure the state passed from Quiz.js
  const {
    total,
    correct,
    percentage,
    details,
    courseTitle,
    registration_id,
    topicId
  } = location.state || {};

  // Build a unique localStorage key per student + quiz
  const storageKey = `attempts_${registration_id}_${topicId}_${courseId}`;

  // Track how many times the student has already reattempted (0, 1, or 2)
  const [reattemptCount, setReattemptCount] = useState(0);

  // On mount, read the stored attempt count
  useEffect(() => {
    const stored = localStorage.getItem(storageKey);
    setReattemptCount(stored ? Number(stored) : 0);
  }, [storageKey]);

  // Trigger confetti only if passed
  useEffect(() => {
    if (percentage >= 60) {
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.6 } });
    }
  }, [percentage]);

  const getScoreColor = (pct) => {
    if (pct >= 80) return 'text-green-600';
    if (pct >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const handleReturnToDashboard = () => {
    navigate('/student-dashboard/home');
  };

  const handleReturnToCourse = () => {
    if (topicId) {
      navigate(
        `/student-dashboard/courses/ongoing/${topicId}/protocols/course-overview/${courseId}`,
        { state: { registration_id } }
      );
    } else {
      handleReturnToDashboard();
    }
  };

  // If required data is missing, show the error state
  if (total == null || topicId == null) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            {!total ? 'No results available' : 'Missing topic information'}
          </h1>
          <p className="text-gray-600 mb-6">
            {!total
              ? "We couldn't find your quiz results."
              : 'Some required information is missing. Please try again.'}
          </p>
          <button
            onClick={handleReturnToDashboard}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Called when “Reattempt Quiz” is clicked
  const handleReattemptClick = () => {
    const nextCount = reattemptCount + 1;
    localStorage.setItem(storageKey, String(nextCount));
    setReattemptCount(nextCount);
    // Go back one step in history (to the quiz)
    navigate(-1);
  };

  const hasFailed = percentage < 60;
  const usedAllReattempts = hasFailed && reattemptCount >= 2;

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {hasFailed ? '😞 Failed' : '🎉 Congratulations! 🎉'}
          </h1>
          <p className="text-xl text-gray-600">
            {hasFailed
              ? `You scored ${percentage}%. Better luck next time.`
              : `You've completed the quiz for ${courseTitle}`}
          </p>
        </div>

        {/* Certificate Notice */}
{percentage >= 60 && (
  <div className="mb-8 text-center">
    <p className="text-blue-700 font-medium">
      ✅ You passed! Your certification will be generated after approval by faculty.
    </p>
  </div>
)}

        {/* Score Card */}
        <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-800">Your Score</h2>
            <div className={`text-5xl font-bold mt-4 ${getScoreColor(percentage)}`}>
              {percentage}%
            </div>
            <p className="text-gray-600 mt-2">
              {correct} correct out of {total} questions
            </p>
          </div>
        </div>

        {/* Detailed Results */}
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-6">Detailed Results</h3>
          <div className="space-y-6">
            {details.map((item, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg ${
                  item.isCorrect 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                <p className="font-medium text-gray-800 mb-2">{item.question}</p>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div className="flex items-center">
                    <span className="text-gray-600 mr-2">Your Answer:</span>
                    <span className={item.isCorrect ? 'text-green-600' : 'text-red-600'}>
                      {item.userAnswer || 'No answer provided'}
                    </span>
                  </div>
                  {!item.isCorrect && (
                    <div className="flex items-center">
                      <span className="text-gray-600 mr-2">Correct Answer:</span>
                      <span className="text-green-600">{item.correctAnswer}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ——— Modified Action Buttons Section ——— */}
        <div className="mt-8 flex justify-center space-x-4">
          {hasFailed && reattemptCount < 2 && (
            <button
              onClick={handleReattemptClick}
              className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Reattempt Quiz
            </button>
          )}

          {hasFailed && reattemptCount >= 2 && (
            <button
              onClick={handleReturnToDashboard}
              className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Return to Dashboard
            </button>
          )}

          {!hasFailed && (
            <button
              onClick={handleReturnToCourse}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Return to Course
            </button>
          )}
        </div>
        {/* ———————————————————————————————— */}
      </div>
    </div>
  );
};

export default QuizResult;
